// src/lib/useOffsetPager.ts
//
// ★★ OFFSET paging state, for the eleven list surfaces the gateway pages with
// `?offset=` rather than with a keyset cursor.
//
// ══ WHY THIS IS NOT `usePager` ══
//
// `usePager` walks a KEYSET cursor and is **forward-only by construction**: a
// cursor names "the row after this one" in one direction, so "previous" is
// answered by replaying a cursor history the hook keeps, and there is no such
// thing as jumping to page seven. That is the right shape for `/api/bills` and
// `/api/records`, where the population is high-volume and append-heavy.
//
// An offset names a POSITION, so this hook can do what the other cannot — go
// back without remembering anything, jump to the last page, and tell the
// officer *"page 3 of 9"* rather than *"page 3"*. It needs the total to do that,
// and every one of these surfaces already answers one under the same predicate
// as its rows.
//
// ⚠ **Two paging hooks is a cost, and it is a deliberate one.** Collapsing them
// would mean either giving the cursor surfaces a page count they cannot compute
// or giving the offset surfaces a forward-only walk they do not need. The
// gateway's own `list-reachability.test.ts` records which surface is which and
// why, so the split is documented in one place rather than inferred from which
// hook a page happened to import.
//
// ⚠ **WHAT AN OFFSET WALK CANNOT PROMISE, stated here because a page-number
// control looks like it promises it.** A row inserted or removed between two
// page fetches shifts every later page by one, so a walk can show a row twice
// or miss it. On the administrator-edited catalogues this hook serves that is a
// few edits a day. On `/api/audit` it is once per page, guaranteed — reading
// the trail appends to the trail — which is why that route reports an `asOf`
// instant and asks a caller walking more than one page to pin `?asOf=` to it.
// `AuditPage` does that; a new caller of this hook against an append-heavy
// surface must too.
//
// ⚠ **`?asOf=`, NOT `?occurredTo=`, and the difference is not cosmetic** (S-075).
// `?occurredTo=` is a date FILTER and is inclusive of the whole millisecond it
// names, because `occurred_at` keeps microseconds while an instant names
// milliseconds. That is right for a filter and wrong for a pin: a pin one
// millisecond wide readmits rows that commit after the anchor was read, so the
// walk can skip again. `?asOf=` is applied exactly.
//
// (A hook, and this app has no component-rendering harness — see
// `useSort.test.ts` for the same reasoning. The state machine is the pure
// functions below, tested directly in `useOffsetPager.test.ts`.)
import { useCallback, useMemo, useState } from 'react'

export interface OffsetPageState {
  /** Rows to skip. 0 on page one. */
  readonly offset: number
  /** Rows per page — the `?limit=` this surface is being asked for. */
  readonly pageSize: number
}

/**
 * The page number an offset lands on, 1-based.
 *
 * ⚠ Derived, never stored. Storing both an offset and a page number is two
 * statements of one fact, and the one that drifts is always the derived-looking
 * one — S-011's shape, in a React hook.
 */
export function pageNumberOf(state: OffsetPageState): number {
  return Math.floor(state.offset / state.pageSize) + 1
}

/**
 * How many pages a population of `total` rows fills.
 *
 * ⚠ **A total of 0 is ONE page, not zero.** An officer looking at an empty
 * table is on a page; telling them "page 1 of 0" is a bug report waiting to be
 * filed, and it makes `canGoNext` arithmetic below need a special case it
 * otherwise does not.
 */
export function pageCountOf(total: number, pageSize: number): number {
  if (pageSize <= 0) return 1
  return Math.max(1, Math.ceil(total / pageSize))
}

/**
 * Clamp an offset into the population.
 *
 * ⚠ Exists because the population can SHRINK under a walk — an officer on page
 * nine of nine when somebody retires four rows must land on the new last page,
 * not on an empty one with no way back except the "first" button. Snapping to
 * the last page's own offset keeps every control meaningful.
 */
export function clampToPopulation(offset: number, total: number, pageSize: number): number {
  if (offset <= 0) return 0
  const lastPageOffset = (pageCountOf(total, pageSize) - 1) * pageSize
  return Math.min(offset, lastPageOffset)
}

export interface UseOffsetPagerResult {
  readonly offset: number
  readonly pageSize: number
  /** 1-based, for display. */
  readonly pageNumber: number
  /**
   * How many pages there are — or `undefined` where the surface cannot say.
   *
   * ⚠ `undefined` is not "none". `GET /api/audit` reports no total (a filtered
   * `count(*)` over the largest append-only table in the product, on every page
   * view, is what P2C14 §6.1.1's 1 s p95 does not survive), so that surface can
   * say *"page 3"* and whether there is a page 4, and cannot say *"of 9"*.
   * Rendering a made-up count would be worse than admitting the gap.
   */
  readonly pageCount: number | undefined
  readonly canGoPrevious: boolean
  readonly canGoNext: boolean
  /** False where there is no total, because the end cannot be located without one. */
  readonly canGoLast: boolean
  readonly goPrevious: () => void
  readonly goNext: () => void
  readonly goFirst: () => void
  readonly goLast: () => void
  /**
   * Back to page one. Call whenever a FILTER or the SORT changes.
   *
   * ⚠ Not optional politeness: page three of an unfiltered list is not page
   * three of a filtered one, and leaving the offset alone across a filter change
   * shows the officer a page that may not exist in the new population — usually
   * an empty table that reads as "no results" when there are plenty.
   */
  readonly reset: () => void
}

/**
 * @param total The population size the CURRENT response reported, under the same
 *   predicate as its rows. `0` while the first request is in flight is correct
 *   and harmless: the controls disable, and re-enable when the answer arrives.
 *
 *   ⚠ **`undefined` is a supported and meaningful answer** — see
 *   {@link UseOffsetPagerResult.pageCount}. `GET /api/audit` does not report a
 *   total, deliberately: `audit.event` is the largest append-only table in the
 *   product and a filtered `count(*)` over it on every page view is exactly the
 *   kind of query P2C14 §6.1.1's 1 s p95 does not survive. Such a surface passes
 *   `hasMore` instead, obtained by asking for one row more than it displays.
 * @param hasMore Used ONLY when `total` is `undefined`: is there at least one
 *   row after this page? Ignored otherwise, because a total answers it exactly.
 */
export function useOffsetPager(
  pageSize: number,
  total: number | undefined,
  hasMore = false,
): UseOffsetPagerResult {
  const [offset, setOffset] = useState(0)

  // ⚠ `undefined` total means the page COUNT is unknowable, not zero. Reporting
  // 1 here would disable "next" on a surface that has ten thousand more rows,
  // which is the defect this whole thread exists to close — so `pageCount` is
  // `undefined` too and every consumer has to decide what to render for it.
  const pageCount = total === undefined ? undefined : pageCountOf(total, pageSize)
  // ⚠ Clamped on READ rather than corrected in an effect. An effect that wrote
  // a new offset would fire a second fetch for a page the officer never asked
  // for, and would race the first one. With no total there is no population to
  // clamp into, so the offset stands as asked.
  const safeOffset = total === undefined ? Math.max(0, offset) : clampToPopulation(offset, total, pageSize)
  const pageNumber = pageNumberOf({ offset: safeOffset, pageSize })

  const goPrevious = useCallback(() => setOffset((o) => Math.max(0, o - pageSize)), [pageSize])
  const goNext = useCallback(() => setOffset((o) => o + pageSize), [pageSize])
  const goFirst = useCallback(() => setOffset(0), [])
  // ⚠ "Last" is unreachable without a total — there is no way to know where the
  // end is without counting. `canGoLast` is what tells the control to omit the
  // button rather than render one that cannot work.
  const goLast = useCallback(
    () => setOffset(total === undefined ? 0 : (pageCountOf(total, pageSize) - 1) * pageSize),
    [total, pageSize],
  )
  const reset = useCallback(() => setOffset(0), [])

  return useMemo(
    () => ({
      offset: safeOffset,
      pageSize,
      pageNumber,
      pageCount,
      canGoPrevious: pageNumber > 1,
      canGoNext: pageCount === undefined ? hasMore : pageNumber < pageCount,
      canGoLast: pageCount !== undefined,
      goPrevious,
      goNext,
      goFirst,
      goLast,
      reset,
    }),
    [safeOffset, pageSize, pageNumber, pageCount, hasMore, goPrevious, goNext, goFirst, goLast, reset],
  )
}
